import { useState } from "react";
import BadgeButton from "../BadgeButton";
import {
  DangerActionButton,
  PrimaryActionButton,
  SecondaryActionButton,
} from "../buttons/ActionButtons";
import Card from "../card";
import EditButton from "../details/EditButton";
import InfoPopover from "../details/InfoPopover";
import PhoneDetailItem from "../details/PhoneDetailItem";
import { ThemedButtonGroup } from "../forms";
import LoadingImage from "../LoadingImage";
import Typography from "../typography";

export const SharedComponentsCard = ({
  setModalExampleOpen,
  setThemedModalOpen,
}: {
  setModalExampleOpen: (open: boolean) => void;
  setThemedModalOpen: (open: boolean) => void;
}) => {
  const [preferredContactMethod, setPreferredContactMethod] = useState("call");
  return (
    <Card className="mb-12 w-full" variant="cream" elevation={2}>
      <Card.Header variant="cream" className="bg-cream-100">
        <Typography variant="h4" className="text-terracotta">
          New Shared Components
        </Typography>
      </Card.Header>
      <Card.Body className="grid gap-8 bg-white md:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-4">
          <Typography variant="subtitle1" className="text-terracotta-700">
            LoadingImage
          </Typography>
          <LoadingImage
            src="/images/app-home-screen.png"
            alt="Harper mobile home screen"
            wrapperClassName="h-48 w-full"
            className="object-cover"
          />
          <Typography variant="body2" className="text-brown-600">
            Wrap remote assets to provide Harper-branded loading and error
            states while preserving layout stability.
          </Typography>
        </div>
        <div className="space-y-4">
          <Typography variant="subtitle1" className="text-terracotta-700">
            ConfirmationModal
          </Typography>
          <BadgeButton
            tone="primary"
            variant="solid"
            onClick={() => setModalExampleOpen(true)}
          >
            Preview confirmation modal
          </BadgeButton>
          <Typography variant="body2" className="text-brown-600">
            Use for high-impact actions such as disabling integrations or
            notifying the care team about operational downtime.
          </Typography>
        </div>
        <div className="space-y-4 md:col-span-2 lg:col-span-1">
          <Typography variant="subtitle1" className="text-terracotta-700">
            ThemedModal
          </Typography>
          <div className="flex flex-wrap gap-3">
            <BadgeButton
              tone="primary"
              variant="solid"
              onClick={() => setThemedModalOpen(true)}
            >
              Open themed modal
            </BadgeButton>
            <EditButton onClick={() => setThemedModalOpen(true)}>
              Edit clinic profile
            </EditButton>
            <EditButton
              tone="neutral"
              onClick={() => setThemedModalOpen(true)}
              ariaLabel="Edit clinic hours"
            />
          </div>
          <Typography variant="body2" className="text-brown-600">
            Use the themed modal for branded workflows where you need a warm
            Harper accent, and pair it with the edit button for quick inline
            detail updates.
          </Typography>
        </div>
        <div className="space-y-4 md:col-span-2">
          <Typography variant="subtitle1" className="text-terracotta-700">
            PhoneDetailItem &amp; ThemedButtonGroup
          </Typography>
          <div className="grid gap-4 lg:grid-cols-2">
            <PhoneDetailItem
              label="Clinic Support Line"
              phoneNumber="(800) 555-0199"
              contactName="After-hours triage"
              helperText="Direct escalation path to the on-call physician"
            />
            <div className="flex flex-col gap-4 rounded-xl border border-beige-200 bg-white p-4 shadow-sm">
              <Typography variant="subtitle2" className="text-terracotta-700">
                Preferred outreach
              </Typography>
              <ThemedButtonGroup
                label="Notify the care team via"
                helperText="We recommend text for quick confirmations."
                options={[
                  {
                    value: "call",
                    label: "Call",
                    description:
                      "Use for urgent escalation or critical downtime.",
                  },
                  {
                    value: "text",
                    label: "Text",
                    description:
                      "Ideal for schedule adjustments and reminders.",
                  },
                  {
                    value: "email",
                    label: "Email",
                    description: "Send a digest to the administrative team.",
                  },
                ]}
                value={preferredContactMethod}
                onChange={setPreferredContactMethod}
                fullWidth
                layout="vertical"
              />
              <Typography variant="caption" className="text-brown-600">
                Current selection: {preferredContactMethod}
              </Typography>
            </div>
          </div>
          <Typography variant="body2" className="text-brown-600">
            Pair the phone detail item with the themed button group to present
            action shortcuts alongside configurable contact preferences.
          </Typography>
        </div>
        <div className="space-y-4 md:col-span-2">
          <Typography variant="subtitle1" className="text-terracotta-700">
            Action Buttons & Info Popover
          </Typography>
          <div className="flex flex-wrap items-center gap-3">
            <PrimaryActionButton>Save scheduling changes</PrimaryActionButton>
            <SecondaryActionButton>Preview announcement</SecondaryActionButton>
            <DangerActionButton>Disable integration</DangerActionButton>
            <InfoPopover
              triggerLabel="Usage tips"
              description="Use the terracotta primary style for irreversible save actions, the beige secondary style for non-destructive previews, and the danger style to highlight risky operations."
              align="start"
            />
          </div>
          <Typography variant="body2" className="text-brown-600">
            Pair buttons with the info popover to surface contextual guidance
            when teammates are reviewing complex admin workflows.
          </Typography>
        </div>
      </Card.Body>
    </Card>
  );
};
